import { PackedGrid } from '@/_shared/components/packed-grid/index.js';
import { Section } from '@/_shared/components/section/index.js';
import { useTsRest } from '@/_shared/context/ts-rest.js';
import {
    Button,
    Divider,
    Skeleton,
    SkeletonItem,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
    makeStyles,
    mergeClasses,
    tokens,
} from '@fluentui/react-components';
import { ChatRegular } from '@fluentui/react-icons';
import { px } from '@this/app-shared/dist/tools/styles.js';
import { fromIso, toHumanReadable } from '@this/shared/dist/tools/datetime.js';
import { Fragment } from 'react';

type CommunicationProps = {
    claimId: string;
    onDocumentsSelect: (documentIds: string[]) => void;
};

const useStyles = makeStyles({
    table: {
        display: 'grid',
        gridTemplateColumns: 'max-content minmax(max-content, 1fr) 3fr',
        gridColumn: '1 / -1',
        alignItems: 'start',
    },
    tableDisplayContents: {
        display: 'contents',
    },
    tableDivider: {
        gridColumn: '1 / -1',
    },
    tableCell: {
        alignContent: 'center',
        height: '100%',
        minHeight: `calc(${tokens.spacingHorizontalXXXL} + ${tokens.spacingHorizontalXS})`,
        paddingInlineStart: px(0),
        paddingInlineEnd: tokens.spacingHorizontalL,
    },
    tableCellHeader: {
        paddingBottom: tokens.spacingVerticalM,
        minHeight: 'max-content',
        height: 'max-content',
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.fontSizeBase200,
        color: tokens.colorNeutralForegroundDisabled,
    },
    tableCellSkeleton: {
        width: '100%',
    },
    tableCellSkeletonItem: {
        height: tokens.fontSizeBase500,
    },
    emptyContainer: {
        gridColumn: '1 / -1',
        paddingTop: tokens.spacingVerticalXS,
        backgroundColor: `${tokens.colorTransparentBackground} !important`,
        border: 'none',
    },
    emptyTable: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: tokens.spacingVerticalM,
        top: tokens.spacingVerticalS,
        padding: tokens.spacingVerticalL,
        width: '100%',
        height: '100%',
        fontWeight: tokens.fontWeightSemibold,
        border: [tokens.strokeWidthThin, 'dashed', tokens.colorNeutralStroke1].join(' '),
        borderRadius: tokens.borderRadiusXLarge,
        backgroundColor: tokens.colorNeutralBackground2,
        userSelect: 'none',
        cursor: 'default',
    },
    badgeContainer: {
        display: 'flex',
        columnGap: tokens.spacingHorizontalS,
        rowGap: tokens.spacingVerticalXS,
        paddingTop: tokens.spacingVerticalXS,
        paddingBottom: tokens.spacingVerticalXS,
        flexWrap: 'wrap',
    },
});

export function Communication({ claimId, onDocumentsSelect }: CommunicationProps) {
    const styles = useStyles();
    const tsr = useTsRest();

    const { data } = tsr.claimDetails.getCommunicationItems.useQuery({
        queryKey: ['claimDetails', 'getCommunicationItems', claimId],
        queryData: {
            params: {
                claimId,
            },
        },
        refetchInterval: 30_000,
    });

    const items = data?.body.items;

    return (
        <Section
            header={{
                label: 'Kommunikation',
                color: 'default',
                icon: <ChatRegular />,
            }}
        >
            <PackedGrid>
                <Table noNativeElements className={styles.table}>
                    <TableHeader className={styles.tableDisplayContents}>
                        <TableRow className={styles.tableDisplayContents}>
                            <TableCell className={mergeClasses(styles.tableCell, styles.tableCellHeader)} />
                            <TableCell className={mergeClasses(styles.tableCell, styles.tableCellHeader)}>
                                Datum
                            </TableCell>
                            <TableCell className={mergeClasses(styles.tableCell, styles.tableCellHeader)}>
                                Inhalt
                            </TableCell>
                        </TableRow>
                    </TableHeader>

                    <TableBody className={styles.tableDisplayContents}>
                        {items === undefined ? (
                            // Loading state
                            Array.from({ length: 5 }, (_, index) => (
                                <Fragment key={index}>
                                    <TableRow className={styles.tableDisplayContents}>
                                        <TableCell className={styles.tableDisplayContents}>
                                            <Divider className={styles.tableDivider} />
                                        </TableCell>
                                    </TableRow>
                                    <TableRow className={styles.tableDisplayContents}>
                                        <TableCell className={styles.tableCell}>
                                            <Skeleton className={styles.tableCellSkeleton}>
                                                <SkeletonItem className={styles.tableCellSkeletonItem} />
                                            </Skeleton>
                                        </TableCell>
                                        <TableCell className={styles.tableCell}>
                                            <Skeleton className={styles.tableCellSkeleton}>
                                                <SkeletonItem className={styles.tableCellSkeletonItem} />
                                            </Skeleton>
                                        </TableCell>
                                        <TableCell className={styles.tableCell}>
                                            <Skeleton className={styles.tableCellSkeleton}>
                                                <SkeletonItem className={styles.tableCellSkeletonItem} />
                                            </Skeleton>
                                        </TableCell>
                                    </TableRow>
                                </Fragment>
                            ))
                        ) : items.length === 0 ? (
                            // Empty state
                            <TableRow className={styles.emptyContainer}>
                                <TableCell className={styles.tableDisplayContents}>
                                    <div className={styles.emptyTable}>Keine Kommunikation vorhanden</div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            // Data rows
                            items.map((item) => (
                                <Fragment key={item.id}>
                                    <TableRow className={styles.tableDisplayContents}>
                                        <TableCell className={styles.tableDisplayContents}>
                                            <Divider className={styles.tableDivider} />
                                        </TableCell>
                                    </TableRow>
                                    <TableRow className={styles.tableDisplayContents}>
                                        <TableCell className={styles.tableCell}>
                                            {item.direction === 'inbound' ? '⬊' : '⬁'}
                                        </TableCell>
                                        <TableCell className={styles.tableCell}>
                                            {toHumanReadable(fromIso(item.date), 'datetime_short_2digit')}
                                        </TableCell>
                                        <TableCell className={styles.tableCell}>
                                            <div className={styles.badgeContainer}>
                                                {item.content.map((contentItem, contentIndex) => (
                                                    <Button
                                                        key={contentIndex}
                                                        appearance="outline"
                                                        shape="rounded"
                                                        size="small"
                                                        onClick={() => onDocumentsSelect(contentItem.documentIds)}
                                                    >
                                                        {contentItem.documentIds.length > 1
                                                            ? `${contentItem.documentIds.length}x `
                                                            : ''}
                                                        {contentItem.type}
                                                    </Button>
                                                ))}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                </Fragment>
                            ))
                        )}
                    </TableBody>
                </Table>
            </PackedGrid>
        </Section>
    );
}
